<template>
  <div>
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <b-card-title class="mb-0">
          일자별 테마
        </b-card-title>
        <b-button
          variant="primary"
          :disabled="loading"
          @click="fetchRecent"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          />
          새로고침
        </b-button>
      </b-card-header>

      <b-card-body>
        <b-alert
          v-if="errorMessage"
          variant="danger"
          show
        >
          {{ errorMessage }}
        </b-alert>

        <div
          v-if="loading"
          class="text-center py-3"
        >
          <b-spinner />
        </div>

        <div
          v-else-if="weekRows.length === 0"
          class="text-center text-muted py-3"
        >
          조회된 일자별 테마가 없습니다.
        </div>

        <div
          v-else
          class="theme-week-list"
        >
          <div
            v-for="row in weekRows"
            :key="row.weekKey"
            class="theme-week-row"
          >
            <div
              v-for="column in weekdayColumns"
              :key="`${row.weekKey}-${column.value}`"
              class="theme-day-cell"
            >
              <div
                v-if="row.days[column.value]"
                class="theme-day-card"
              >
                <div class="theme-day-head">
                  <span>{{ formatDateLabel(row.days[column.value]) }} ({{ row.days[column.value].dayOfWeek }})</span>
                  <button
                    type="button"
                    class="theme-toggle-button"
                    @click="toggleDay(row.days[column.value].baseYmd)"
                  >
                    <span class="theme-toggle-icon">{{ isExpanded(row.days[column.value].baseYmd) ? '▴' : '▾' }}</span>
                  </button>
                </div>
                <div class="theme-day-body">
                  <div
                    v-for="theme in visibleThemes(row.days[column.value])"
                    :key="`${row.days[column.value].baseYmd}-${theme.themeNo}`"
                    class="theme-entry"
                    role="button"
                    tabindex="0"
                    @click="openThemeDetail(row.days[column.value].baseYmd, theme.themeNo)"
                    @keydown.enter.prevent="openThemeDetail(row.days[column.value].baseYmd, theme.themeNo)"
                    @keydown.space.prevent="openThemeDetail(row.days[column.value].baseYmd, theme.themeNo)"
                  >
                    <div class="theme-main-line">
                      <span class="theme-name-badge">
                        <strong class="theme-name">{{ theme.themeName }}</strong>
                      </span>
                      <span :class="['theme-rate', rateClass(theme.changeRate)]">{{ formatRate(theme.changeRate) }}</span>
                    </div>
                    <p class="theme-leader-line">
                      <span
                        v-for="(leader, index) in leaderItems(theme)"
                        :key="`${theme.themeNo}-leader-${index}`"
                        class="theme-leader-token"
                      >
                        <span class="theme-leader-token-name">{{ leader.name }}</span><span :class="['theme-leader-token-rate', rateClass(leader.rate)]">({{ formatRate(leader.rate) }})</span>
                      </span>
                    </p>
                  </div>
                </div>
                <div class="theme-rank-summary">
                  <button
                    v-if="themeCount(row.days[column.value]) > 3"
                    type="button"
                    class="theme-more-inline"
                    @click="toggleDay(row.days[column.value].baseYmd)"
                  >
                    {{ isExpanded(row.days[column.value].baseYmd) ? '접기' : '더보기' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <b-modal
      v-model="detailModalVisible"
      size="lg"
      hide-footer
      :title="detailTitle"
    >
      <b-alert
        v-if="detailErrorMessage"
        variant="danger"
        show
      >
        {{ detailErrorMessage }}
      </b-alert>

      <div
        v-if="detailLoading"
        class="text-center py-3"
      >
        <b-spinner />
      </div>

      <b-table
        v-else
        :items="detailItems"
        :fields="detailFields"
        responsive
        small
        show-empty
        empty-text="상세 종목이 없습니다."
        @row-clicked="openChartModal"
      >
        <template #cell(currentPrice)="row">
          {{ formatNumber(row.item.currentPrice) }}
        </template>
        <template #cell(prevDiff)="row">
          {{ formatSignedNumber(row.item.prevDiff) }}
        </template>
        <template #cell(fluctuationRate)="row">
          <span :class="rateClass(row.item.fluctuationRate)">{{ formatRate(row.item.fluctuationRate) }}</span>
        </template>
        <template #cell(tradeAmountMillion)="row">
          {{ formatTradeAmountEok(row.item.tradeAmountMillion) }}
        </template>
      </b-table>
    </b-modal>

    <b-modal
      id="theme-daily-stock-chart-modal"
      v-model="chartModalVisible"
      size="xl"
      :title="`${currentChartLabel} 차트`"
      hide-footer
    >
      <div class="mb-1 font-weight-bold">
        {{ selectedItem.name }} ({{ selectedItem.code }})
      </div>

      <div class="mb-1 d-flex align-items-center">
        <span class="mr-50">차트구분</span>
        <b-form-select
          v-model="chartType"
          :options="chartTypeOptions"
          size="sm"
          style="width: 140px;"
        />
        <template v-if="chartType === 'M'">
          <span class="ml-1 mr-50">분봉</span>
          <b-form-select
            v-model="minuteScope"
            :options="minuteScopeOptions"
            size="sm"
            style="width: 140px;"
          />
        </template>
      </div>

      <b-alert
        v-if="chartErrorMessage"
        variant="danger"
        show
      >
        {{ chartErrorMessage }}
      </b-alert>

      <div
        v-if="chartLoading"
        class="text-center py-2"
      >
        <b-spinner />
      </div>

      <vue-apex-charts
        v-else
        type="candlestick"
        height="420"
        :options="chartOptions"
        :series="chartSeries"
      />
    </b-modal>
  </div>
</template>

<script>
import axios from '@axios'
import VueApexCharts from 'vue-apexcharts'
import {
  BAlert,
  BButton,
  BCard,
  BCardBody,
  BCardHeader,
  BCardTitle,
  BFormSelect,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCardBody,
    BCardHeader,
    BCardTitle,
    BFormSelect,
    BModal,
    BSpinner,
    BTable,
    VueApexCharts,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      recentDays: [],
      expandedDays: {},
      detailCache: {},
      detailModalVisible: false,
      detailLoading: false,
      detailErrorMessage: '',
      chartErrorMessage: '',
      selectedBaseYmd: '',
      selectedThemeNo: null,
      selectedThemeName: '',
      detailItems: [],
      chartLoading: false,
      chartModalVisible: false,
      selectedItem: {
        code: '',
        name: '',
      },
      chartSeries: [{ data: [] }],
      chartType: 'D',
      chartTypeOptions: [
        { value: 'M', text: '분봉' },
        { value: 'D', text: '일봉' },
        { value: 'W', text: '주봉' },
        { value: 'MO', text: '월봉' },
      ],
      minuteScope: '3',
      minuteScopeOptions: [
        { value: '1', text: '1분봉' },
        { value: '3', text: '3분봉' },
        { value: '5', text: '5분봉' },
        { value: '10', text: '10분봉' },
      ],
      chartOptions: {
        chart: {
          type: 'candlestick',
          toolbar: { show: true },
        },
        plotOptions: {
          candlestick: {
            colors: {
              upward: '#ff3b30',
              downward: '#2f80ed',
            },
          },
        },
        xaxis: {
          type: 'category',
        },
        tooltip: {
          x: {
            formatter: value => value || '',
          },
        },
        yaxis: {
          tooltip: { enabled: true },
          labels: {
            formatter: value => Number(value).toLocaleString(),
          },
        },
        noData: {
          text: '차트 데이터가 없습니다.',
        },
      },
      weekdayColumns: [
        { value: 1, label: '월' },
        { value: 2, label: '화' },
        { value: 3, label: '수' },
        { value: 4, label: '목' },
        { value: 5, label: '금' },
      ],
      detailFields: [
        { key: 'itemRankNo', label: '순번' },
        { key: 'stockCode', label: '종목코드' },
        { key: 'stockName', label: '종목명' },
        { key: 'currentPrice', label: '현재가' },
        { key: 'prevDiff', label: '전일비' },
        { key: 'fluctuationRate', label: '등락률' },
        { key: 'tradeAmountMillion', label: '거래대금(억)' },
      ],
    }
  },
  computed: {
    weekRows() {
      const rows = []
      const weekMap = new Map()
      this.recentDays.forEach(day => {
        const weekKey = this.getWeekStart(day.baseYmd)
        if (!weekMap.has(weekKey)) {
          weekMap.set(weekKey, {
            weekKey,
            weekStart: weekKey,
            days: {},
          })
          rows.push(weekMap.get(weekKey))
        }
        const weekday = this.getWeekdayValue(day.baseYmd)
        if (weekday >= 1 && weekday <= 5) {
          weekMap.get(weekKey).days[weekday] = day
        }
      })
      return rows
    },
    detailTitle() {
      if (!this.selectedThemeName) return '테마 상세'
      return `${this.selectedBaseYmd} ${this.selectedThemeName}`
    },
    currentChartLabel() {
      const found = this.chartTypeOptions.find(option => option.value === this.chartType)
      return found ? found.text : '일봉'
    },
  },
  watch: {
    chartType() {
      if (this.chartModalVisible && this.selectedItem.code) {
        this.fetchChart()
      }
    },
    minuteScope() {
      if (this.chartModalVisible && this.selectedItem.code && this.chartType === 'M') {
        this.fetchChart()
      }
    },
  },
  created() {
    this.fetchRecent()
  },
  methods: {
    parseApiResponse(response) {
      const payload = response && response.data ? response.data : null
      if (!payload || payload.success !== true) {
        throw new Error(payload && payload.error ? payload.error : '요청 처리 중 오류가 발생했습니다.')
      }
      return payload.data
    },
    async fetchRecent() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await axios.get('/api/kiwoom/naver-theme-batch/recent')
        const data = this.parseApiResponse(response)
        this.recentDays = Array.isArray(data.days) ? data.days : []
      } catch (error) {
        this.recentDays = []
        this.errorMessage = error && error.message ? error.message : '일자별 테마 조회에 실패했습니다.'
      } finally {
        this.loading = false
      }
    },
    visibleThemes(day) {
      const themes = Array.isArray(day && day.themes) ? day.themes : []
      return this.isExpanded(day && day.baseYmd) ? themes : themes.slice(0, 3)
    },
    themeCount(day) {
      const themes = Array.isArray(day && day.themes) ? day.themes : []
      return themes.length
    },
    isExpanded(baseYmd) {
      return this.expandedDays[baseYmd] === true
    },
    toggleDay(baseYmd) {
      this.$set(this.expandedDays, baseYmd, !this.isExpanded(baseYmd))
    },
    async openThemeDetail(baseYmd, themeNo) {
      this.detailModalVisible = true
      this.detailLoading = true
      this.detailErrorMessage = ''
      this.detailItems = []
      this.selectedBaseYmd = baseYmd
      this.selectedThemeNo = themeNo

      try {
        let dayData = this.detailCache[baseYmd]
        if (!dayData) {
          const response = await axios.post('/api/kiwoom/naver-theme-batch/date', { baseYmd })
          dayData = this.parseApiResponse(response)
          this.$set(this.detailCache, baseYmd, dayData)
        }
        const theme = (dayData.themes || []).find(item => item.themeNo === themeNo)
        if (!theme) {
          throw new Error('선택한 테마 상세를 찾을 수 없습니다.')
        }
        this.selectedThemeName = theme.themeName || ''
        this.detailItems = Array.isArray(theme.items) ? theme.items : []
      } catch (error) {
        this.selectedThemeName = ''
        this.detailItems = []
        this.detailErrorMessage = error && error.message ? error.message : '테마 상세 조회에 실패했습니다.'
      } finally {
        this.detailLoading = false
      }
    },
    async openChartModal(item) {
      if (!item || !item.stockCode) return

      this.selectedItem = {
        code: item.stockCode,
        name: item.stockName || '',
      }
      this.chartType = 'D'
      this.minuteScope = '3'
      this.chartModalVisible = true
      await this.fetchChart()
    },
    async fetchChart() {
      if (!this.selectedItem.code) return

      this.chartErrorMessage = ''
      this.chartSeries = [{ data: [] }]
      this.chartLoading = true

      try {
        const response = await axios.post('/api/kiwoom/stock-item/chart/daily', {
          code: this.selectedItem.code,
          chartType: this.chartType,
          ticScope: this.chartType === 'M' ? this.minuteScope : undefined,
          count: 120,
        })
        const data = this.parseApiResponse(response)
        const candles = Array.isArray(data.candles) ? data.candles : []
        const seriesData = candles
          .map(candle => {
            const x = this.formatCategoryDate(candle.dt)
            if (!x) return null
            return {
              x,
              y: [
                this.toNumber(candle.openPrice),
                this.toNumber(candle.highPrice),
                this.toNumber(candle.lowPrice),
                this.toNumber(candle.closePrice),
              ],
            }
          })
          .filter(Boolean)

        this.chartSeries = [{ data: seriesData }]
      } catch (error) {
        this.chartErrorMessage = error && error.message ? error.message : '차트 조회에 실패했습니다.'
      } finally {
        this.chartLoading = false
      }
    },
    getWeekdayValue(baseYmd) {
      const date = this.parseBaseDate(baseYmd)
      const day = date.getDay()
      return day === 0 ? 7 : day
    },
    getWeekStart(baseYmd) {
      const date = this.parseBaseDate(baseYmd)
      const weekday = this.getWeekdayValue(baseYmd)
      const monday = new Date(date)
      monday.setDate(date.getDate() - weekday + 1)
      return this.toDateKey(monday)
    },
    parseBaseDate(baseYmd) {
      const year = Number(baseYmd.slice(0, 4))
      const month = Number(baseYmd.slice(4, 6)) - 1
      const day = Number(baseYmd.slice(6, 8))
      return new Date(year, month, day)
    },
    toDateKey(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}${month}${day}`
    },
    formatDateLabel(day) {
      const baseYmd = day && day.baseYmd ? day.baseYmd : ''
      if (!baseYmd || baseYmd.length !== 8) return baseYmd
      return `${baseYmd.slice(4, 6)}.${baseYmd.slice(6, 8)}`
    },
    toNumber(value) {
      if (value === null || value === undefined) return 0
      const cleaned = String(value).replace(/,/g, '')
      const parsed = Number(cleaned)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    formatCategoryDate(value) {
      const text = String(value || '').trim()
      if (text.length === 14) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)} ${text.slice(8, 10)}:${text.slice(10, 12)}`
      }
      if (text.length >= 8) {
        return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`
      }
      return text
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(String(value).replace(/,/g, ''))
      if (Number.isNaN(parsed)) return value
      return parsed.toLocaleString()
    },
    formatSignedNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(String(value).replace(/,/g, ''))
      if (Number.isNaN(parsed)) return value
      if (parsed > 0) return `+${parsed.toLocaleString()}`
      return parsed.toLocaleString()
    },
    formatRate(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return value
      const prefix = parsed > 0 ? '+' : ''
      return `${prefix}${parsed.toFixed(2)}%`
    },
    formatTradeAmountEok(value) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return value
      const amountInEok = parsed / 100
      const digits = amountInEok >= 100 ? 0 : 1
      return amountInEok.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    },
    leaderItems(theme) {
      return [
        { name: theme.leaderStock1Name, rate: theme.leaderStock1Rate },
        { name: theme.leaderStock2Name, rate: theme.leaderStock2Rate },
      ].filter(item => item.name)
    },
    formatDecimal(value, digits = 1) {
      if (value === null || value === undefined || value === '') return '-'
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return value
      return parsed.toFixed(digits)
    },
    rateClass(value) {
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return 'text-body'
      if (parsed > 0) return 'text-danger'
      if (parsed < 0) return 'text-primary'
      return 'text-body'
    },
  },
}
</script>

<style scoped>
.theme-week-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.theme-week-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 1rem;
}

.theme-day-cell {
  min-height: 1px;
}

.theme-day-card {
  height: 100%;
  border: 1px solid #ebe9f1;
  border-radius: 0.75rem;
  background: #fff;
  box-shadow: 0 2px 12px rgba(34, 41, 47, 0.04);
  overflow: hidden;
}

.theme-day-head {
  padding: 0.75rem 0.9rem;
  background: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
  font-size: 0.95rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.theme-day-body {
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.theme-rank-summary {
  padding: 0 0.75rem 0.75rem;
}

.theme-more-inline {
  margin-top: 0.7rem;
  border: 1px dashed #d8d6de;
  background: #f8f8f8;
  color: #5e5873;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.8rem;
}

.theme-rank-link {
  border: 1px solid #e5e3eb;
  background: #f3f2f7;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  text-align: center;
  color: #5e5873;
  font-size: 0.8rem;
  line-height: 1.3;
}

.theme-rank-link:hover {
  color: #7367f0;
}

.theme-entry {
  width: 100%;
  text-align: left;
  border: 1px solid #ebe9f1;
  border-radius: 0.65rem;
  background: #fff;
  padding: 0.7rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: normal !important;
  cursor: pointer;
}

.theme-entry:hover {
  border-color: #7367f0;
  background: #f9f8ff;
}

.theme-toggle-button {
  border: none;
  background: transparent;
  color: #7367f0;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
  padding: 0;
}

.theme-toggle-icon {
  display: inline-block;
  min-width: 1rem;
  text-align: center;
}

.theme-main-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  overflow: visible !important;
}

.theme-name-badge {
  display: inline-flex;
  align-items: center;
  max-width: calc(100% - 4.5rem);
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  background: #5e5873;
  border: 1px solid #4b4560;
  overflow: visible !important;
}

.theme-name {
  color: #fff;
  font-size: 0.96rem;
  font-weight: 800;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
}

.theme-rate {
  font-size: 0.88rem;
  font-weight: 700;
  white-space: nowrap;
}

.theme-leader-line {
  margin: 0;
  color: #6e6b7b;
  font-size: 0.79rem;
  line-height: 1.35;
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  word-break: keep-all;
}

.theme-leader-token + .theme-leader-token {
  margin-left: 0.45rem;
}

.theme-leader-token-name {
  color: #5e5873;
}

.theme-leader-token-rate {
  font-weight: 700;
}

.theme-entry *,
.theme-leader-line * {
  overflow: visible !important;
  text-overflow: clip !important;
}

@media (max-width: 1200px) {
  .theme-week-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .theme-week-row {
    grid-template-columns: 1fr;
  }
}
</style>
